import { useEffect, useState } from 'react'

import { useCenterLine } from '@/context/CenterLineContext'
import { useUAContext } from '@/context/UAContext'

type UseDwellPlaybackOptions = {
  vodId?: string
}

/**
 * Centre-line autoplay hook for mobile.
 *
 * Registers the card element with the CenterLineProvider. When the card is closest to the viewport
 * centre, playback activates immediately — HoverVideoPreview applies a short render delay
 * (HOVER_RENDER_DELAY_MS, 50ms) to filter accidental activations.
 *
 * Playback stops when a closer card takes over, when the card scrolls away
 * from the centre line, or when the page becomes hidden.
 *
 * Desktop hover behaviour is unaffected — the hook only activates on
 * coarse-pointer (touch) devices.
 */
export function useDwellPlayback({ vodId }: UseDwellPlaybackOptions) {
  const [isPlaying, setIsPlaying] = useState(false)
  const { isCoarsePointer: isTouchDevice, isPageVisible } = useUAContext()
  const [element, setElement] = useState<Element | null>(null)

  const centerLine = useCenterLine()

  const ref = (node: Element | null) => setElement(node)
  const activate = () => setIsPlaying(true)
  const deactivate = () => setIsPlaying(false)

  // Register / unregister with the centre-line observer
  useEffect(() => {
    if (!isTouchDevice || !vodId || !element || !isPageVisible) {
      setIsPlaying(false)
      return
    }

    if (!centerLine) return

    return centerLine.observe(element, activate, deactivate)
  }, [vodId, element, isPageVisible, isTouchDevice, centerLine])

  return { ref, isPlaying }
}
